import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { sendQuickActionGoogleReview } from './patientQuickActionsService'

// Sends the clinic's configured Google review link to the patient. If the clinic owner hasn't set
// one up, the backend returns a clear error which we surface here.
export default function SendGoogleReviewDialog({ open, onClose, contactContext, patientName, onSent }) {
  const [sending, setSending] = useState(false)
  const resolvedName = patientName || 'the patient'

  const handleSend = useCallback(async () => {
    setSending(true)
    const result = await sendQuickActionGoogleReview(contactContext)
    setSending(false)
    if (result?.ok) {
      const channels = []
      if (result.emailSent) channels.push('email')
      if (result.whatsappSent) channels.push('WhatsApp')
      toast.success(
        channels.length ? `Google review link sent via ${channels.join(' & ')}.` : 'Google review link sent.',
      )
      onSent?.()
      onClose?.()
    } else {
      toast.error(result?.message || 'Unable to send the Google review link.')
    }
  }, [contactContext, onClose, onSent])

  return (
    <Dialog open={!!open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <RateReviewOutlinedIcon sx={{ color: '#4338ca' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Send Google review link</Typography>
        <IconButton
          onClick={onClose}
          disabled={sending}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ mb: 1 }}>
          Send your clinic&apos;s Google review link to <strong>{resolvedName}</strong> over WhatsApp and
          email?
        </Typography>
        <Alert severity="info">
          Uses the Google review link your clinic owner has configured. If none is set up yet, add it under
          the clinic&apos;s Google Business settings.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{ textTransform: 'none', bgcolor: '#4338ca', '&:hover': { bgcolor: '#3730a3' } }}
        >
          {sending ? 'Sending…' : 'Send review link'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
